import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Printer, FileText, CheckCircle, Users, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const ClearanceCertificate = () => {
  const [certificates, setCertificates] = useState([]);
  const [checkedOutStudents, setCheckedOutStudents] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('certificates');
  const printRef = useRef();

  useEffect(() => {
    fetchCertificates();
    fetchCheckedOutStudents();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await apiClient.get('/api/hostel/clearance-certificates');
      setCertificates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCheckedOutStudents = async () => {
    try {
      const response = await apiClient.get('/api/hostel/students');
      const students = Array.isArray(response.data) ? response.data : [];
      setCheckedOutStudents(students.filter(s => s.status === 'CheckedOut'));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handlePrint = (cert) => {
    setSelectedCert(cert);
    setIsPrintModalOpen(true);
  };

  const printCertificate = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Clearance Certificate - ${selectedCert?.certificate_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header h2 { margin: 10px 0 0 0; font-size: 18px; color: #666; }
            .cert-number { text-align: right; font-size: 14px; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; font-size: 14px; color: #333; margin-bottom: 10px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            .row { display: flex; margin-bottom: 8px; }
            .label { width: 150px; font-weight: bold; }
            .value { flex: 1; }
            .amount-section { background: #f5f5f5; padding: 15px; margin-top: 20px; }
            .amount-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
            .total { font-size: 16px; font-weight: bold; border-top: 1px solid #000; padding-top: 10px; margin-top: 10px; }
            .status { text-align: center; margin: 30px 0; padding: 15px; font-size: 18px; font-weight: bold; }
            .status.cleared { background: #d4edda; color: #155724; border: 2px solid #155724; }
            .footer { margin-top: 50px; display: flex; justify-content: space-between; }
            .signature { text-align: center; width: 200px; }
            .signature-line { border-top: 1px solid #000; margin-top: 50px; padding-top: 5px; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Hostel Clearance & Checkout Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="certificates">Clearance Certificates</TabsTrigger>
              <TabsTrigger value="checkout-report">Checkout Report</TabsTrigger>
            </TabsList>

            {/* Clearance Certificates Tab */}
            <TabsContent value="certificates">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Certificate #</th>
                    <th className="text-left p-2">Student</th>
                    <th className="text-left p-2">Hostel</th>
                    <th className="text-left p-2">Checkout Date</th>
                    <th className="text-right p-2">Fee Paid</th>
                    <th className="text-right p-2">Fines</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.length === 0 ? (
                    <tr><td colSpan="8" className="text-center p-4 text-gray-500">No certificates found. Certificates are auto-generated when students checkout.</td></tr>
                  ) : (
                    certificates.map((cert) => (
                      <tr key={cert.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{cert.certificate_number}</td>
                        <td className="p-2">{cert.student_name}</td>
                        <td className="p-2">{cert.hostel_name} - {cert.room_number}</td>
                        <td className="p-2">{formatDate(cert.checkout_date)}</td>
                        <td className="p-2 text-right text-green-600">₹{cert.total_fee_paid}</td>
                        <td className="p-2 text-right">₹{cert.fine_amount || 0}</td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${cert.clearance_status === 'Cleared' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {cert.clearance_status}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <Button size="sm" variant="outline" onClick={() => handlePrint(cert)}>
                            <Printer className="w-3 h-3 mr-1" />Print
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TabsContent>

            {/* Checkout Report Tab */}
            <TabsContent value="checkout-report">
              <div className="mb-4">
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800 font-medium">Total Checked Out Students: {checkedOutStudents.length}</span>
                  </div>
                </Card>
              </div>
              
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Student Name</th>
                    <th className="text-left p-2">Student ID</th>
                    <th className="text-left p-2">Hostel</th>
                    <th className="text-left p-2">Room</th>
                    <th className="text-left p-2">Admission Date</th>
                    <th className="text-left p-2">Checkout Date</th>
                    <th className="text-right p-2">Monthly Fee</th>
                    <th className="text-center p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {checkedOutStudents.length === 0 ? (
                    <tr><td colSpan="8" className="text-center p-4 text-gray-500">No checked out students found.</td></tr>
                  ) : (
                    checkedOutStudents.map((student) => (
                      <tr key={student.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{student.student_name}</td>
                        <td className="p-2">{student.student_id}</td>
                        <td className="p-2">{student.hostel_name}</td>
                        <td className="p-2">{student.room_number}</td>
                        <td className="p-2">{formatDate(student.admission_date)}</td>
                        <td className="p-2">{student.checkout_date ? formatDate(student.checkout_date) : '-'}</td>
                        <td className="p-2 text-right">₹{student.monthly_fee}</td>
                        <td className="p-2 text-center">
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                            Checked Out
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Print Certificate Modal */}
      <Dialog open={isPrintModalOpen} onOpenChange={setIsPrintModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Clearance Certificate</DialogTitle>
          </DialogHeader>
          
          {selectedCert && (
            <>
              <div ref={printRef} className="p-4 bg-white">
                {/* Tenant Header */}
                <div className="text-center mb-4 border-b pb-3">
                  {selectedCert.tenant?.logo_url && (
                    <img src={selectedCert.tenant.logo_url} alt="Logo" className="h-12 mx-auto mb-2" />
                  )}
                  <h2 className="text-lg font-bold">{selectedCert.tenant?.college_name || selectedCert.tenant?.name || 'Institution Name'}</h2>
                  <p className="text-sm text-gray-600">{selectedCert.tenant?.address || ''}</p>
                  <p className="text-xs text-gray-500">
                    {selectedCert.tenant?.phone && `Phone: ${selectedCert.tenant.phone}`}
                    {selectedCert.tenant?.email && ` | Email: ${selectedCert.tenant.email}`}
                  </p>
                </div>

                <div className="header">
                  <h1>HOSTEL CLEARANCE CERTIFICATE</h1>
                  <h2>{selectedCert.hostel_name}</h2>
                </div>
                
                <div className="cert-number">
                  <strong>Certificate No:</strong> {selectedCert.certificate_number}
                </div>
                
                <div className="section">
                  <div className="section-title">Student Information</div>
                  <div className="row"><span className="label">Name:</span><span className="value">{selectedCert.student_name}</span></div>
                  <div className="row"><span className="label">Student ID:</span><span className="value">{selectedCert.student_id}</span></div>
                  <div className="row"><span className="label">Room Number:</span><span className="value">{selectedCert.room_number}</span></div>
                </div>
                
                <div className="section">
                  <div className="section-title">Stay Period</div>
                  <div className="row"><span className="label">Admission Date:</span><span className="value">{formatDate(selectedCert.admission_date)}</span></div>
                  <div className="row"><span className="label">Checkout Date:</span><span className="value">{formatDate(selectedCert.checkout_date)}</span></div>
                </div>
                
                <div className="amount-section">
                  <div className="section-title">Financial Summary</div>
                  <div className="amount-row"><span>Total Fee Paid:</span><span>₹{selectedCert.total_fee_paid}</span></div>
                  <div className="amount-row"><span>Fines Amount:</span><span>₹{selectedCert.fine_amount || 0}</span></div>
                  <div className="amount-row"><span>Fines Paid:</span><span>₹{selectedCert.fine_paid || 0}</span></div>
                  <div className="amount-row"><span>Damage Charges:</span><span>₹{selectedCert.damage_charges || 0}</span></div>
                  <div className="amount-row total"><span>Pending Dues:</span><span>₹{selectedCert.pending_dues || 0}</span></div>
                </div>
                
                <div className={`status ${selectedCert.clearance_status === 'Cleared' ? 'cleared' : ''}`}>
                  <CheckCircle style={{display: 'inline', marginRight: '10px', verticalAlign: 'middle'}} />
                  CLEARANCE STATUS: {selectedCert.clearance_status?.toUpperCase()}
                </div>
                
                <div className="footer">
                  <div className="signature">
                    <div className="signature-line">Student Signature</div>
                  </div>
                  <div className="signature">
                    <div className="signature-line">Warden Signature</div>
                  </div>
                  <div className="signature">
                    <div className="signature-line">Accounts Signature</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setIsPrintModalOpen(false)}>Close</Button>
                <Button onClick={printCertificate}>
                  <Printer className="w-4 h-4 mr-2" />Print Certificate
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClearanceCertificate;
